import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthSession, User } from '@fanvote25/shared';
import { ApiRequestError, api, getToken, setToken } from './api';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  signIn(email: string, password: string): Promise<User>;
  signUp(input: SignUpInput): Promise<User>;
  signOut(): void;
  updateUser(patch: Partial<User> & Record<string, unknown>): Promise<User>;
  refresh(): Promise<void>;
}

export interface SignUpInput {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  favoriteTeamId?: string;
  state?: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { user: me } = await api.get<{ user: User }>('/auth/me');
      setUser(me);
    } catch (error) {
      // An expired or revoked token should log the voter out quietly rather
      // than wedging the app behind an error screen.
      if (error instanceof ApiRequestError && error.status === 401) {
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const session = await api.post<AuthSession>('/auth/login', { email, password });
      setToken(session.token);
      setUser(session.user);
      await queryClient.invalidateQueries();
      return session.user;
    },
    [queryClient],
  );

  const signUp = useCallback(
    async (input: SignUpInput) => {
      const session = await api.post<AuthSession>('/auth/signup', input);
      setToken(session.token);
      setUser(session.user);
      await queryClient.invalidateQueries();
      return session.user;
    },
    [queryClient],
  );

  const signOut = useCallback(() => {
    setToken(null);
    setUser(null);
    queryClient.clear();
  }, [queryClient]);

  const updateUser = useCallback(
    async (patch: Partial<User> & Record<string, unknown>) => {
      const { user: next } = await api.patch<{ user: User }>('/auth/me', patch);
      setUser(next);
      return next;
    },
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAdmin: user?.role === 'admin',
      signIn,
      signUp,
      signOut,
      updateUser,
      refresh,
    }),
    [user, loading, signIn, signUp, signOut, updateUser, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
